import React, {Fragment, SyntheticEvent, useEffect, useState} from "react";
import {useHistory, useParams} from "react-router-dom";
import * as _ from 'lodash'

// Logic.
import {useDispatch} from "react-redux";


// Design.
import {
    FormChips, GenericFormContainer, Leap4Breadcrumbs, SmartInput, Spinner,
    Grid, MuiIcons
} from "@leap4/frontend";
import {CreateOrganizationDto, UpdateOrganizationDto} from "@leap4/auth";

import {add, deleteInstance, getById, update} from "../../../actions/generic-crud-actions";
import {getAllOrganizations} from "../../../actions/users";
import {setAlert} from "../../../actions/alert";
import {AlertTypes} from "../../../utils/alert.utils";
import {IOrganization} from "./IOrganization"
import {IOrganizationPartners, PublisherLoginsContainer} from "./PublisherLogins/PublisherLogins.container";
import {initialOrganizationObj} from "./Organization";

// Mode enum - was previously imported from internal path
enum Mode {
    Create = "create",
    Edit = "edit"
}

const { Toc: TocIcon, Create: CreateIcon, AutoFixNormal: AutoFixNormalIcon, GpsFixed: GpsFixedIcon } = MuiIcons;


const AddEditOrganization = (): JSX.Element => {
    const history = useHistory();
    const params = useParams<{ id?: string }>();
    const dispatch = useDispatch();

    const schemaName = "organization";
    const addInstanceRoute = `/${schemaName}s/add`;
    const editInstanceRoute = `/${schemaName}s/`;

    const [organization, setOrganization] = useState<IOrganization>(initialOrganizationObj)
    const [isAddPage, setIsAddPage] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [allOrganizations, setAllOrganizations] = useState<IOrganization[]>([])

    const handleFormChange = (e: any) => {
        let {name, value} = e.target;
        setOrganization({...organization, [name]: value});
    }

    const handleContactsChange = (contacts: string[]) => {
        setOrganization({
            ...organization, metadata: {
                publisherLogin: {
                    ...organization.metadata.publisherLogin,
                },
                adOpsContacts: contacts
            }
        })
    }

    // Form submit.
    const handleSubmit = async (ev: SyntheticEvent) => {
        // For block rendering.
        ev?.preventDefault();
        if (!organization) {
            return;
        }


        if (isAddPage) {
            delete organization._id
            await add<CreateOrganizationDto>(organization, schemaName, history, dispatch);
        } else if (params.id && params.id != "add") {
            await update<UpdateOrganizationDto>(params.id, organization, schemaName, history);
        }

    };

    // Init form.
    useEffect(() => {
        (async () => {
            setIsLoading(true);
            const id = params.id;
            // For editUser page.
            if (id && id !== "add") {
                setIsAddPage(false);
                const instanceData = await getById(id, schemaName, dispatch);
                _.defaultsDeep(instanceData, initialOrganizationObj)
                setOrganization({...instanceData});
            }

            setIsLoading(false);
        })()
    }, []);

    useEffect(() => {
        let loadedOrganizations;
        (async () => {
            try {
                setIsLoading(true);
                loadedOrganizations = await getAllOrganizations(dispatch) as IOrganization[]
                setAllOrganizations(loadedOrganizations)
            } catch (err) {
                setAlert("Couldn't load organizations", AlertTypes.FAILURE)
            } finally {
                setIsLoading(false);
            }
        })()
    }, []);

    const updatePublisherLogins = (publisherLogins: IOrganizationPartners[]) => {
        const publisherLoginsForState = publisherLogins.reduce((acc: any, currVal) => {
            const organizationId: string = currVal.org._id
            acc[organizationId] = {
                partners: currVal.selectedPartners,
                toSkipDiscrepancy: currVal.toSkipDiscrepancy
            };
            return acc;
        }, {})

        setOrganization({
            ...organization,
            metadata: {...organization.metadata, publisherLogin: publisherLoginsForState}
        })
    }

    const isCreateDisabled = () => {
        let _isCreatedDisabled = false;
        const publisherLoginOrgIds = Object.keys(organization.metadata.publisherLogin)
        if (publisherLoginOrgIds.some(orgId => organization.metadata.publisherLogin[orgId]?.partners?.length === 0)) _isCreatedDisabled = true;

        return _isCreatedDisabled;
    }

    if(isLoading) return (
        <Spinner />
    )

    return (
        <Fragment>
            <Leap4Breadcrumbs breadcrumbs={[
                {
                    text: `${schemaName[0].toUpperCase() + schemaName.slice(1)}s Dashboard`,
                    icon: <TocIcon/>,
                    link: `/${schemaName}s`
                },
                {
                    text: isAddPage ? "Create" : "Update",
                    link: isAddPage ? addInstanceRoute: editInstanceRoute,
                    icon: isAddPage ? <CreateIcon/> : <AutoFixNormalIcon/>
                },
                {
                    text: organization.name || "New Organization",
                    link: "",
                    icon: <GpsFixedIcon/>
                }
            ]}/>
            {isLoading || !allOrganizations.length ? (
                <Spinner/>
            ) : (
                <GenericFormContainer
                    disabled={isLoading || isCreateDisabled()}
                    onDeleteFunction={() => deleteInstance(String(params.id), schemaName, history)}
                    mode={isAddPage ? Mode.Create : Mode.Edit}
                    onCreateOrEditFunc={((ev: any) => handleSubmit(ev))}>

                    <Grid container spacing={2} sm={12}>

                        <Grid item xs={12}>

                            <SmartInput
                                helperText={"Name"}
                                onChange={handleFormChange}
                                value={organization?.name}
                                muiProps={{fullWidth: true, name: "name"}}

                            />

                        </Grid>

                        <Grid item xs={12} style={{paddingTop: "10px"}}>
                            <FormChips optionsList={[]} valueKey={""} labelKey={""}
                                       stackProps={{style: {width: "100%"}}}
                                       onUpdateItems={handleContactsChange}
                                       inputName={"AdOps Contacts"} enableCreate={true}
                                       chips={[...organization.metadata.adOpsContacts]}
                                       pureCreate={true}/>
                        </Grid>
                    </Grid>

                    <PublisherLoginsContainer allOrganizations={allOrganizations}
                                              updatePublisherLogins={updatePublisherLogins}
                                              currentOrganization={organization}
                    />

                </GenericFormContainer>


            )}
        </Fragment>
    );
}


export default AddEditOrganization;
